// src/components/learning/FicheEleveProgression.js
// Fiche de progression d'un élève — visible par le prof (lecture seule)
// Accessible depuis la liste des cours / réservations dans ModernProfDashboard

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, Target, X, MessageSquare, ClipboardList } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;

const LETTRES_ABJAD = [
  { lettre: 'ا', nom: 'الأَلِف', transliteration: 'Alif' },
  { lettre: 'ب', nom: 'الباء', transliteration: 'Ba' },
  { lettre: 'ت', nom: 'التاء', transliteration: 'Ta' },
  { lettre: 'ث', nom: 'الثاء', transliteration: 'Tha' },
  { lettre: 'ج', nom: 'الجيم', transliteration: 'Jim' },
  { lettre: 'ح', nom: 'الحاء', transliteration: 'Ha' },
  { lettre: 'خ', nom: 'الخاء', transliteration: 'Kha' },
  { lettre: 'د', nom: 'الدال', transliteration: 'Dal' },
  { lettre: 'ذ', nom: 'الذال', transliteration: 'Dhal' },
  { lettre: 'ر', nom: 'الراء', transliteration: 'Ra' },
  { lettre: 'ز', nom: 'الزاي', transliteration: 'Zayn' },
  { lettre: 'س', nom: 'السين', transliteration: 'Sin' },
  { lettre: 'ش', nom: 'الشين', transliteration: 'Shin' },
  { lettre: 'ص', nom: 'الصاد', transliteration: 'Sad' },
  { lettre: 'ض', nom: 'الضاد', transliteration: 'Dad' },
  { lettre: 'ط', nom: 'الطاء', transliteration: 'Ta emp.' },
  { lettre: 'ظ', nom: 'الظاء', transliteration: 'Dha emp.' },
  { lettre: 'ع', nom: 'العين', transliteration: 'Ayn' },
  { lettre: 'غ', nom: 'الغين', transliteration: 'Ghayn' },
  { lettre: 'ف', nom: 'الفاء', transliteration: 'Fa' },
  { lettre: 'ق', nom: 'القاف', transliteration: 'Qaf' },
  { lettre: 'ك', nom: 'الكاف', transliteration: 'Kaf' },
  { lettre: 'ل', nom: 'اللام', transliteration: 'Lam' },
  { lettre: 'م', nom: 'الميم', transliteration: 'Mim' },
  { lettre: 'ن', nom: 'النون', transliteration: 'Nun' },
  { lettre: 'ه', nom: 'الهاء', transliteration: 'Ha' },
  { lettre: 'و', nom: 'الواو', transliteration: 'Waw' },
  { lettre: 'ي', nom: 'الياء', transliteration: 'Ya' },
];

const GREEN = '#065f46';

const statutLabel = {
  maitrisee: 'Maîtrisée', en_cours: 'En cours', vue: 'Vue', non_abordee: 'À faire',
};

const chipStyle = (s) =>
  s === 'maitrisee' ? { background: '#059669', color: '#fff',     border: '2px solid #059669' }
: s === 'en_cours'  ? { background: '#ffffff', color: '#8a6a33', border: '2px solid #c99a5b' }
: s === 'vue'       ? { background: '#d1fae5', color: '#065f46', border: '2px solid #d1fae5' }
:                     { background: '#f1efe9', color: '#b9b6ac', border: '2px solid #f1efe9' };

const pill = (active) => ({
  borderRadius: 20, padding: '7px 14px', fontSize: 12.5, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: active ? '#059669' : '#f1efe9',
  color: active ? '#fff' : '#9aa79f',
});

export default function FicheEleveProgression({ eleveId, eleveName, token, onClose }) {
  const [progression, setProgression] = useState([]);
  const [stats, setStats] = useState(null);
  const [historique, setHistorique] = useState([]); // historique complet des commentaires
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    if (!eleveId) { setLoading(false); return; } // pas d'id élève → ne pas mouliner indéfiniment
    setLoading(true);
    axios.get(`${API}/progression/eleve/${eleveId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (res.data.success) {
        setProgression(res.data.progression || []);
        setStats(res.data.stats);
        setHistorique(res.data.historique_commentaires || []);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [eleveId, token]);

  const lettresAvecStatut = LETTRES_ABJAD.map(l => {
    const prog = progression.find(p => p.lettre === l.lettre);
    let statut = 'non_abordee';
    if (prog) {
      if (prog.statut === 'maitrisee') statut = 'maitrisee';
      else if (prog.statut === 'en_cours') statut = 'en_cours';
      else if (prog.lettre_vue) statut = 'vue';
    }
    return { ...l, statut, prog };
  });

  const pct = stats?.pourcentage || 0;
  const prochaine = lettresAvecStatut.find(l => l.statut === 'en_cours')
                 || lettresAvecStatut.find(l => l.statut === 'non_abordee');

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 99999,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 20,
        width: '100%', maxWidth: 720, maxHeight: '92vh',
        overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
        fontFamily: "'DM Sans', sans-serif",
      }}>

        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${GREEN}, #047857)`,
          borderRadius: '20px 20px 0 0', padding: '20px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#fff',
        }}>
          <div>
            <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 4 }}>Fiche de progression</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{eleveName || 'Élève'}</div>
            <div style={{ fontSize: 13, opacity: 0.75, marginTop: 2 }}>
              Niveau 1 · Lettres — {stats?.maitrisees || 0} / 28 acquises
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ position: 'relative', width: 64, height: 64 }}>
              <svg width="64" height="64" style={{ transform: 'rotate(-90deg)' }}>
                <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="6"/>
                <circle cx="32" cy="32" r="26" fill="none" stroke="#fff" strokeWidth="6"
                  strokeDasharray={`${2 * Math.PI * 26}`}
                  strokeDashoffset={`${2 * Math.PI * 26 * (1 - pct / 100)}`}
                  strokeLinecap="round"
                />
              </svg>
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)', fontSize: 13, fontWeight: 700, color: '#fff',
              }}>{pct}%</div>
            </div>
            <button onClick={onClose} aria-label="Fermer" style={{
              width: 34, height: 34, borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.1)',
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><X size={18} strokeWidth={2} /></button>
          </div>
        </div>

        <div style={{ padding: 24 }}>

          {/* Niveaux */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <span style={pill(true)}>Niveau 1 · Lettres</span>
            <span style={pill(false)}><Lock size={13} strokeWidth={2} />Niveau 2 · Mots</span>
            <span style={pill(false)}><Lock size={13} strokeWidth={2} />Niveau 3 · Phrases</span>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>Chargement…</div>
          ) : (
            <>
              {/* Alphabet compact — une ligne */}
              <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 4, overflowX: 'auto', paddingBottom: 4 }}>
                {lettresAvecStatut.map(l => (
                  <div key={l.lettre}
                    title={`${l.transliteration} — ${statutLabel[l.statut]}`}
                    style={{
                      flex: '0 0 auto', width: 28, height: 28, borderRadius: 8,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: "'Amiri', 'Traditional Arabic', serif", fontSize: 16,
                      direction: 'rtl', lineHeight: 1, ...chipStyle(l.statut),
                    }}>
                    {l.lettre}
                  </div>
                ))}
              </div>

              {/* Prochaine à évaluer */}
              {prochaine && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13, color: '#6b7c74' }}>
                  <Target size={16} strokeWidth={2} color="#c99a5b" />
                  Prochaine à évaluer :
                  <b style={{ fontFamily: "'Amiri', serif", fontSize: 18 }}>{prochaine.lettre}</b>
                  <span style={{ color: '#9aa79f' }}>({prochaine.transliteration})</span>
                </div>
              )}

              {/* Légende */}
              <div style={{ display: 'flex', gap: 14, marginTop: 10, marginBottom: 4, fontSize: 11.5, color: '#9aa79f', flexWrap: 'wrap' }}>
                <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#059669', marginRight: 5 }} />acquise</span>
                <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#c99a5b', marginRight: 5 }} />en cours</span>
                <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#e6e9e4', marginRight: 5 }} />à faire</span>
              </div>

              {/* Notes des professeurs — HISTORIQUE COMPLET (tous les profs, datés) */}
              {historique.length > 0 && (
                <div style={{ background: '#f4faf7', border: '1px solid #d1fae5', borderRadius: 12, padding: '16px 20px', marginTop: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: GREEN, marginBottom: 12 }}>
                    <MessageSquare size={16} strokeWidth={2} />Notes des professeurs
                  </div>
                  {historique.map((h, i, arr) => (
                    <div key={i} style={{
                      display: 'flex', gap: 10, marginBottom: 10, paddingBottom: 10,
                      borderBottom: i < arr.length - 1 ? '0.5px solid #e5e7eb' : 'none',
                    }}>
                      <span style={{ fontSize: 20, color: GREEN, fontFamily: "'Amiri', serif", direction: 'rtl', minWidth: 28, textAlign: 'center' }}>{h.lettre}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, color: '#1a1a1a' }}>{h.commentaire}</div>
                        <div style={{ fontSize: 11, color: '#9aa79f', marginTop: 2 }}>
                          {h.prof}{h.date ? ` · ${new Date(h.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {progression.length === 0 && (
                <div style={{ textAlign: 'center', padding: '32px 20px', color: '#6b7280', fontSize: 14, marginTop: 12 }}>
                  <ClipboardList size={30} strokeWidth={1.6} color="#b9b6ac" style={{ marginBottom: 8 }} />
                  <div>Aucune progression enregistrée pour cet élève.</div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
